import React, { useState, useMemo } from "react";
import { LuBot, LuBarChart3, LuRefreshCw, LuCalendar } from "react-icons/lu";
import { Select } from "src/components/Form/select";
import Button from "src/components/Button/Button";
import { FaFilter, FaChartBar, FaExclamationTriangle } from "react-icons/fa";
import { languageTuples } from "src/utils/util";
import { UserLangCode } from "src/utils/util.types";
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from "recharts";
import { useTrendData } from "../../hooks/useTrendData";
import { TrendChartLoading } from "./trend-chart-loading";
import { TrendDataFilters } from "../../services/trend-data.service";
import { EnhancedFilterState } from "../../pages/enhanced-dashboard";

type Props = {
  filters?: EnhancedFilterState;
};

const chartTypeOptions = [
  { value: "line", label: "Line Chart" },
  { value: "area", label: "Area Chart" },
  { value: "bar", label: "Bar Chart" },
];

const timeGranularityOptions = [
  { value: "daily", label: "Daily (Last 30 Days)" },
  { value: "weekly", label: "Weekly (Last 12 Weeks)" },
  { value: "custom", label: "Custom Date Range" },
];

const aiSessionStatusOptions = [
  { value: "ALL", label: "All Statuses" },
  { value: "ACTIVE", label: "Active" },
  { value: "COMPLETED", label: "Completed" },
];

export const EnhancedAISessionsStatsSection: React.FC<Props> = () => {
  const [sectionFilters, setSectionFilters] = useState({
    chartType: "line",
    timeGranularity: "daily",
    language: undefined as UserLangCode | undefined,
    aiSessionStatus: "ALL" as "ALL" | "ACTIVE" | "COMPLETED",
    showFilters: false,
    startDate: undefined as Date | undefined,
    endDate: new Date() as Date | undefined,
  });

  const apiFilters: TrendDataFilters = useMemo(() => ({
    timeGranularity: sectionFilters.timeGranularity as "daily" | "weekly" | "custom",
    metric: "ai-sessions",
    role: "ALL",
    language: sectionFilters.language,
    aiSessionStatus: sectionFilters.aiSessionStatus,
    startDate: sectionFilters.startDate,
    endDate: sectionFilters.endDate,
  }), [sectionFilters.timeGranularity, sectionFilters.language, sectionFilters.aiSessionStatus, sectionFilters.startDate, sectionFilters.endDate]);

  const { data: trendData, loading, error, refetch, isInitialLoading } = useTrendData(apiFilters);

  const handleSectionFilterChange = (key: string, value: string | boolean) => {
    setSectionFilters(prev => ({ ...prev, [key]: value }));
  };

  const toggleFilters = () => {
    setSectionFilters(prev => ({ ...prev, showFilters: !prev.showFilters }));
  };

  const renderTooltip = (props: any) => {
    const { active, payload, label } = props;
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-medium text-gray-900">{label}</p>
          {payload.map((entry: any, index: number) => (
            <p key={index} style={{ color: entry.color }} className="text-sm">
              {entry.name}: {entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const renderChart = () => {
    if (!trendData?.data) return null;

    const commonProps = {
      data: trendData.data,
      margin: { top: 5, right: 30, left: 20, bottom: 5 },
    };

    switch (sectionFilters.chartType) {
      case "line":
        return (
          <ResponsiveContainer width="100%" height={400}>
            <LineChart {...commonProps}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" stroke="#6b7280" fontSize={12} />
              <YAxis stroke="#6b7280" fontSize={12} />
              <Tooltip content={renderTooltip} />
              <Legend />
              <Line type="monotone" dataKey="total" stroke="#ec4899" strokeWidth={3} dot={{ fill: "#ec4899", strokeWidth: 2, r: 5 }} activeDot={{ r: 7 }} />
            </LineChart>
          </ResponsiveContainer>
        );
      case "area":
        return (
          <ResponsiveContainer width="100%" height={400}>
            <AreaChart {...commonProps}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" stroke="#6b7280" fontSize={12} />
              <YAxis stroke="#6b7280" fontSize={12} />
              <Tooltip content={renderTooltip} />
              <Legend />
              <Area type="monotone" dataKey="total" stroke="#ec4899" fill="#ec4899" fillOpacity={0.3} strokeWidth={3} />
            </AreaChart>
          </ResponsiveContainer>
        );
      case "bar":
        return (
          <ResponsiveContainer width="100%" height={400}>
            <BarChart {...commonProps}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" stroke="#6b7280" fontSize={12} />
              <YAxis stroke="#6b7280" fontSize={12} />
              <Tooltip content={renderTooltip} />
              <Legend />
              <Bar dataKey="total" fill="#ec4899" />
            </BarChart>
          </ResponsiveContainer>
        );
      default:
        return null;
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center gap-2 mb-6">
        <LuBot className="w-5 h-5 text-gray-700" />
        <h2 className="text-lg font-semibold text-gray-900">AI Sessions Activity Trends</h2>
      </div>

      <div className="flex items-center justify-end mb-6">
        <Button
          type="button"
          onClick={toggleFilters}
          className="bg-gradient-to-r from-pink-500 to-pink-600 hover:from-pink-600 hover:to-pink-700 text-white px-4 py-2.5 rounded-xl flex items-center gap-2.5 text-sm font-medium shadow-lg shadow-pink-500/25 hover:shadow-pink-500/40 transition-all duration-200 transform hover:scale-105"
        >
          <FaFilter className="text-sm" />
          {sectionFilters.showFilters ? "Hide" : "Show"} Filters
        </Button>
      </div>

      {sectionFilters.showFilters && (
        <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
            <FaChartBar className="text-gray-500" />
            Chart Configuration
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                <LuBarChart3 className="text-gray-500" />
                Chart Type
              </label>
              <Select className="w-full" options={chartTypeOptions} label="" getOptionValue={(option) => option.value} getOptionLabel={(option) => option.label} onChange={(val) => handleSectionFilterChange("chartType", String(val) || "line")} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                <LuCalendar className="text-gray-500" />
                Time Granularity
              </label>
              <Select className="w-full" options={timeGranularityOptions} label="" getOptionValue={(option) => option.value} getOptionLabel={(option) => option.label} onChange={(val) => handleSectionFilterChange("timeGranularity", String(val) || "daily")} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                <LuCalendar className="text-gray-500" />
                Language
              </label>
              <Select className="w-full" options={languageTuples} label="" includeOption="All Languages" getOptionValue={(tuple) => tuple[0]} getOptionLabel={(tuple) => tuple[1]} onChange={(val) => handleSectionFilterChange("language", val as UserLangCode)} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                <LuBot className="text-gray-500" />
                Status
              </label>
              <Select className="w-full" options={aiSessionStatusOptions} label="" getOptionValue={(option) => option.value} getOptionLabel={(option) => option.label} onChange={(val) => handleSectionFilterChange("aiSessionStatus", String(val) || "ALL")} />
            </div>
            {sectionFilters.timeGranularity === "custom" && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                    <LuCalendar className="text-gray-500" />
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={sectionFilters.startDate ? sectionFilters.startDate.toISOString().split('T')[0] : ""}
                    max={sectionFilters.endDate ? sectionFilters.endDate.toISOString().split('T')[0] : undefined}
                    onChange={(e) => {
                      const startDate = e.target.value ? new Date(e.target.value) : undefined;
                      setSectionFilters(prev => ({
                        ...prev,
                        startDate,
                        endDate: prev.endDate && startDate && startDate > prev.endDate ? undefined : prev.endDate
                      }));
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                    <LuCalendar className="text-gray-500" />
                    End Date
                  </label>
                  <input
                    type="date"
                    value={sectionFilters.endDate ? sectionFilters.endDate.toISOString().split('T')[0] : ""}
                    min={sectionFilters.startDate ? sectionFilters.startDate.toISOString().split('T')[0] : undefined}
                    max={new Date().toISOString().split('T')[0]}
                    onChange={(e) => {
                      const endDate = e.target.value ? new Date(e.target.value) : undefined;
                      setSectionFilters(prev => ({
                        ...prev,
                        endDate
                      }));
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500"
                  />
                </div>
                <div className="col-span-full">
                  <div className="text-xs text-gray-500 bg-yellow-50 p-2 rounded border border-yellow-200">
                    <p><strong>Note:</strong> Large date ranges may take longer to load. For best performance, keep ranges under 90 days.</p>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
        <div className="mb-4">
          <h4 className="text-lg font-semibold text-gray-800 mb-2">AI Sessions Activity Trends</h4>
        </div>

        {isInitialLoading && (
          <TrendChartLoading chartType={sectionFilters.chartType as "line" | "area" | "bar"} timeGranularity={sectionFilters.timeGranularity} metric="ai-sessions" />
        )}

        {error && !isInitialLoading && (
          <div className="h-96 flex items-center justify-center">
            <div className="text-center">
              <FaExclamationTriangle className="text-red-500 text-4xl mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Failed to Load AI Sessions Data</h3>
              <p className="text-gray-600 mb-4">{error}</p>
              <Button type="button" onClick={refetch} className="bg-pink-600 hover:bg-pink-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 mx-auto">
                <LuRefreshCw className="text-sm" />
                Retry
              </Button>
            </div>
          </div>
        )}

        {!isInitialLoading && !error && (
          loading ? (
            <TrendChartLoading chartType={sectionFilters.chartType as "line" | "area" | "bar"} timeGranularity={sectionFilters.timeGranularity} metric="ai-sessions" />
          ) : (
            renderChart()
          )
        )}
      </div>
    </div>
  );
};
